import { escapeRegExp } from '@sveltia/utils/string';
import { get } from 'svelte/store';

import { entryDraft } from '$lib/services/contents/draft';
import { getField, LIST_KEY_PATH_REGEX } from '$lib/services/contents/entry/fields';
import { getOrCreate } from '$lib/services/utils/cache';

/**
 * @import {
 * EntryDraft,
 * FlattenedEntryContent,
 * InternalLocaleCode,
 * LocaleContentMap,
 * } from '$lib/types/private';
 * @import { FieldKeyPath, ObjectField, ListField } from '$lib/types/public';
 */

/**
 * Cache of pre-compiled regexes keyed by cleaned key path.
 * @type {Map<string, RegExp>}
 */
const expanderRegexCache = new Map();
/**
 * Expander state changes accumulated by {@link syncExpanderStates}, waiting to be written to
 * {@link entryDraft}. `undefined` when no flush is scheduled.
 * @type {Record<FieldKeyPath, boolean> | undefined}
 */
let pendingExpanderStates;

/**
 * Apply the accumulated expander state changes to the entry draft in a single store update.
 */
const flushExpanderStates = () => {
  // Always set, because the flush is only scheduled right after the map is created
  const stateMap = /** @type {Record<FieldKeyPath, boolean>} */ (pendingExpanderStates);

  pendingExpanderStates = undefined;

  entryDraft.update((_draft) => {
    if (_draft) {
      Object.entries(stateMap).forEach(([keyPath, expanded]) => {
        _draft.expanderStates._[keyPath] = expanded;
      });
    }

    return _draft;
  });
};

/**
 * Get the initial object/list expander state based on the `collapsed` option. If `collapsed` is set
 * to `auto`, it checks if there are any values in the object. Otherwise, it uses the `collapsed`
 * option directly, which defaults to `false` (expanded).
 * @param {object} args Arguments.
 * @param {string} args.key Key path of the item. For a List field, it’s a key path of the list
 * item, e.g. `authors.0`. For an Object field, it’s a key path of the object with the `#` suffix,
 * e.g. `details#`.
 * @param {InternalLocaleCode} args.locale Locale code.
 * @param {boolean | 'auto'} [args.collapsed] The `collapsed` option value.
 * @returns {boolean} Whether th expander should be expanded.
 */
export const getInitialExpanderState = ({ key, locale, collapsed = false }) => {
  const _draft = get(entryDraft);
  // A state that is queued but not written yet is still the authoritative one
  const currentState = pendingExpanderStates?.[key] ?? _draft?.expanderStates?._[key];

  if (currentState !== undefined) {
    return currentState;
  }

  if (collapsed === 'auto') {
    const valueMap = _draft?.currentValues?.[locale] ?? {};
    const cleanKey = key.replace(/#$/, '');

    // Pre-compile and cache the regex — same key path is queried on every editor render.
    const regex = getOrCreate(
      expanderRegexCache,
      cleanKey,
      () => new RegExp(`^${escapeRegExp(cleanKey)}\\.[^\\.]+$`),
    );

    return !Object.entries(valueMap).some(([keyPath, value]) => regex.test(keyPath) && !!value);
  }

  return !collapsed;
};

/**
 * Sync the field object/list expander states between locales.
 * @param {Record<FieldKeyPath, boolean>} stateMap Map of key path and state.
 */
export const syncExpanderStates = (stateMap) => {
  const currentStates = get(entryDraft)?.expanderStates?._;

  if (!currentStates) {
    return;
  }

  const changes = Object.entries(stateMap).filter(
    ([keyPath, expanded]) =>
      (pendingExpanderStates?.[keyPath] ?? currentStates[keyPath]) !== expanded,
  );

  // Writing to the store notifies every subscriber, which re-renders the whole editor. Object/List
  // editors call this as they mount, so revealing n of them — by expanding a field or merely
  // scrolling — would otherwise cost O(n²). Drop no-op changes, and coalesce the rest into a single
  // store write per tick, which is enough to collapse a whole mount storm into one update.
  if (!changes.length) {
    return;
  }

  if (!pendingExpanderStates) {
    pendingExpanderStates = {};
    queueMicrotask(flushExpanderStates);
  }

  Object.assign(pendingExpanderStates, Object.fromEntries(changes));
};

/**
 * Get a list of keys for the expander states, given the key path. The returned keys could include
 * nested lists and objects.
 * @param {object} args Partial arguments for {@link getField}.
 * @param {string} args.collectionName Collection name.
 * @param {string} [args.fileName] Collection file name. File/singleton collection only.
 * @param {FlattenedEntryContent} args.valueMap Object holding current entry values.
 * @param {FieldKeyPath} args.keyPath Key path, e.g. `testimonials.0.authors.2.foo`.
 * @param {boolean} [args.isIndexFile] Whether the corresponding entry is the collection’s special
 * index file used specifically in Hugo.
 * @returns {string[]} Keys, e.g. `['testimonials', 'testimonials.0', 'testimonials.0.authors',
 * 'testimonials.0.authors.2', 'testimonials.0.authors.2.foo']`.
 */
export const getExpanderKeys = ({
  collectionName,
  fileName,
  valueMap,
  keyPath,
  isIndexFile = false,
}) => {
  const keys = new Set();
  const getFieldArgs = { collectionName, fileName, valueMap, isIndexFile };

  keyPath.split('.').forEach((_keyPart, index, arr) => {
    const _keyPath = arr.slice(0, index + 1).join('.');
    const config = getField({ ...getFieldArgs, keyPath: _keyPath });
    const endingWithNumber = LIST_KEY_PATH_REGEX.test(_keyPath);

    if (config?.widget === 'object') {
      if (endingWithNumber) {
        keys.add(_keyPath);
      }

      keys.add(`${_keyPath}#`);
    } else if (config?.widget === 'list') {
      keys.add(endingWithNumber ? _keyPath : `${_keyPath}#`);
    } else if (index > 0) {
      const parentKeyPath = arr.slice(0, index).join('.');
      const parentConfig = getField({ ...getFieldArgs, keyPath: parentKeyPath });

      if (
        parentConfig?.widget === 'object' &&
        'fields' in /** @type {ObjectField} */ (parentConfig)
      ) {
        keys.add(`${parentKeyPath}.${parentConfig.name}#`);
      }

      if (parentConfig?.widget === 'list' && 'field' in /** @type {ListField} */ (parentConfig)) {
        keys.add(_keyPath);
      }
    }
  });

  return [...keys];
};

/**
 * Expand any invalid fields, including the parent list/object(s).
 * @param {object} args Partial arguments for {@link getField}.
 * @param {string} args.collectionName Collection name.
 * @param {string} [args.fileName] Collection file name. File/singleton collection only.
 * @param {LocaleContentMap} args.currentValues Field values.
 */
export const expandInvalidFields = ({ collectionName, fileName, currentValues }) => {
  const { validities, isIndexFile } = /** @type {EntryDraft} */ (get(entryDraft));
  /** @type {Record<FieldKeyPath, boolean>} */
  const stateMap = {};

  Object.entries(validities ?? {}).forEach(([locale, validityMap]) => {
    Object.entries(validityMap).forEach(([keyPath, { valid }]) => {
      if (!valid) {
        getExpanderKeys({
          collectionName,
          fileName,
          valueMap: currentValues[locale],
          keyPath,
          isIndexFile,
        }).forEach((key) => {
          stateMap[key] = true;
        });
      }
    });
  });

  syncExpanderStates(stateMap);
};

/**
 * Highlight the corresponding field in the editor by posting a message to the window.
 * @param {object} args Arguments object.
 * @param {InternalLocaleCode} args.locale The locale of the field to highlight.
 * @param {FieldKeyPath} args.keyPath The key path of the field to highlight.
 */
export const highlightEditorField = ({ locale, keyPath }) => {
  window.postMessage(
    { type: 'highlight-editor-field', payload: { locale, keyPath } },
    window.location.origin,
  );
};
